import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Download, Mail, Phone, Filter, Wrench, Box, Shield, ArrowRight, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const FiltrationPartsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Replacement Parts & Accessories | CTC Waterworks"
        description="Complete parts catalog for CTC filtration products. Screens, brushes, gaskets, wedges, heads, bodies, bearings, seals, spray bars, and accessories for BIG, TIG, SCS2, PSF, and BWB filters."
        canonical="https://www.ctcwaterworks.com/parts"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Parts & Accessories</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Main Content - Left 3/4 */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Package className="text-ctc-blue mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      Replacement Parts & Accessories
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Screens, brushes, gaskets, wedges, heads, bodies, bearings/seals, spray bars, and more.
                </p>

                <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg">
                  <p className="text-gray-700 leading-relaxed">
                    <span className="font-semibold text-gray-900">Parts Availability:</span> All replacement parts are stocked by authorized dealers or available direct from CTC Waterworks. Standard lead times: in-stock items ship within 1-3 business days, custom fabrications 2-4 weeks. For part numbers, current availability, and pricing, contact your local dealer or CTC engineering.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Sidebar - Quick Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Navigation</h3>

                <nav className="space-y-2">
                  <a
                    href="#inline-screens"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Inline Filter Screens
                  </a>
                  <a
                    href="#brush-assemblies"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Brush Assemblies
                  </a>
                  <a
                    href="#vacuum-nozzles"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Vacuum Nozzles/Arms
                  </a>
                  <a
                    href="#gaskets-orings"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Gaskets & O-Rings
                  </a>
                  <a
                    href="#blue-wedges"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Blue Wedges
                  </a>
                  <a
                    href="#heads-bodies"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    Heads & Bodies
                  </a>
                  <a
                    href="#scs2-parts"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    SCS2 Intake Parts
                  </a>
                  <a
                    href="#psf-accessories"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    PSF Accessories
                  </a>
                  <a
                    href="#bwb-parts"
                    className="block text-sm text-gray-700 hover:text-ctc-blue hover:bg-gray-50 p-2 rounded transition-colors"
                  >
                    BWB Water Box Parts
                  </a>
                </nav>

                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/contact?topic=parts"
                    className="block w-full bg-ctc-blue text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center text-sm"
                  >
                    <Mail className="inline-block mr-2 mb-1" size={16} />
                    Request Parts Quote
                  </Link>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Full Parts Catalog (PDF)</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related to Cleaning Options */}
      <section className="py-8 bg-blue-50 border-y border-blue-200">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Related to Cleaning Options (BIG, BIG-S, GNG)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Brush Assemblies (BIF)</h4>
              <p className="text-sm text-gray-600 mb-3">Manual brush-in-filter cleaning kits</p>
              <Link to="/filtration/inline/cleaning/semi-auto" className="text-xs text-ctc-blue hover:underline">Semi-Automatic Details →</Link>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Manual & Automated Vacuum Assemblies (MVC)</h4>
              <p className="text-sm text-gray-600 mb-3">Vacuum cleaning mechanisms</p>
              <Link to="/filtration/inline/cleaning/semi-auto" className="text-xs text-ctc-blue hover:underline">Semi-Automatic Details →</Link>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">RAB Kits/Components</h4>
              <p className="text-sm text-gray-600 mb-3">Rotary auto brush assemblies</p>
              <Link to="/filtration/inline/cleaning/automatic" className="text-xs text-green-600 hover:underline">Fully Automatic Details →</Link>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Replacement Screens & Seals</h4>
              <p className="text-sm text-gray-600 mb-3">Filter screens and gasket components</p>
              <Link to="/filtration/inline/cleaning" className="text-xs text-ctc-blue hover:underline">Compare All Options →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* BIF Kits - BIG Series */}
      <section id="bif-kits" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Brush-in-Filter (BIF) Kits — BIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Manual brush cleaning kits for BIG Series inline filters. Hand-crank operated cleaning mechanism.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3" M1</div>
              <div className="text-sm text-ctc-blue">BA540</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3" M3</div>
              <div className="text-sm text-ctc-blue">BA540</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4" M1</div>
              <div className="text-sm text-ctc-blue">BA541</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4" M3</div>
              <div className="text-sm text-ctc-blue">BA541</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA542</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-ctc-blue">BA543</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" S</div>
              <div className="text-sm text-ctc-blue">BA544</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" M</div>
              <div className="text-sm text-ctc-blue">BA545</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" L</div>
              <div className="text-sm text-ctc-blue">BA546</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">12" / 3200</div>
              <div className="text-sm text-ctc-blue">BA616</div>
            </div>
          </div>
        </div>
      </section>

      {/* RAB Kits - BIG Series */}
      <section id="rab-kits" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Retractable Auto Brush (RAB) — BIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Automatic brush cleaning systems for BIG Series. Sensor or timer-triggered automated cleaning.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3"</div>
              <div className="text-sm text-ctc-blue">BA510</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4"</div>
              <div className="text-sm text-ctc-blue">BA511</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA512</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-ctc-blue">BA513</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" S</div>
              <div className="text-sm text-ctc-blue">BA514</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" M</div>
              <div className="text-sm text-ctc-blue">BA515</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" L</div>
              <div className="text-sm text-ctc-blue">BA516</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">12" / 3200</div>
              <div className="text-sm text-ctc-blue">BA616A</div>
            </div>
          </div>
        </div>
      </section>

      {/* BIF Kits - TIG Series */}
      <section id="tig-bif-kits" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Brush-in-Filter (BIF) Kits — TIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Manual brush cleaning kits for TIG Series inline filters. Hand-crank operated cleaning mechanism.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3"</div>
              <div className="text-sm text-ctc-blue">BA593</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4"</div>
              <div className="text-sm text-ctc-blue">BA593</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA636</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-gray-500 italic">Contact for availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* RAB Kits - TIG Series */}
      <section id="tig-rab-kits" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Retractable Auto Brush (RAB) — TIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Automatic brush cleaning systems for TIG Series. Sensor or timer-triggered automated cleaning.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3"</div>
              <div className="text-sm text-ctc-blue">BA598</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4"</div>
              <div className="text-sm text-ctc-blue">BA599</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA520</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-gray-500 italic">Contact for availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* MVC Kits - BIG Series */}
      <section id="mvc-kits" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-orange-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Manual Vacuum Clean (MVC) — BIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Manual vacuum cleaning kits for BIG Series. Hand-crank operated vacuum cleaning cycles.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4" M1</div>
              <div className="text-sm text-ctc-blue">BA611</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4" M3</div>
              <div className="text-sm text-ctc-blue">BA611</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA610</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-ctc-blue">BA609</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" S</div>
              <div className="text-sm text-ctc-blue">BA608</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" M</div>
              <div className="text-sm text-ctc-blue">BA597</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" L</div>
              <div className="text-sm text-ctc-blue">BA596</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">12" / 3200</div>
              <div className="text-sm text-ctc-blue">BA767</div>
            </div>
          </div>
        </div>
      </section>

      {/* AVC Kits - BIG Series */}
      <section id="avc-kits" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-orange-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Auto Vacuum Clean (AVC) — BIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Automatic vacuum cleaning drive units for BIG Series. Sensor-driven automated cleaning cycles.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4" (M1/M3)</div>
              <div className="text-sm text-ctc-blue">BA594</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">BA602</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-ctc-blue">BA604</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" S</div>
              <div className="text-sm text-ctc-blue">BA605</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" M</div>
              <div className="text-sm text-ctc-blue">BA680</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" L</div>
              <div className="text-sm text-ctc-blue">BA675</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">Jumbo M</div>
              <div className="text-sm text-ctc-blue">BA680</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">Jumbo L</div>
              <div className="text-sm text-ctc-blue">BA675</div>
            </div>
          </div>
        </div>
      </section>

      {/* BIG Series Screens */}
      <section id="big-screens" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Filter className="text-ctc-blue mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">BIG Series Screens</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement screens for BIG Series inline filters by size. 304 stainless steel construction.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">3"</div>
              <div className="text-sm text-ctc-blue">A4047</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">4"</div>
              <div className="text-sm text-ctc-blue">A4052</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">6"</div>
              <div className="text-sm text-ctc-blue">A4080</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">8"</div>
              <div className="text-sm text-ctc-blue">B4103</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" S</div>
              <div className="text-sm text-ctc-blue">B4033S</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" M</div>
              <div className="text-sm text-ctc-blue">B4033M</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">10" L</div>
              <div className="text-sm text-ctc-blue">B4033L</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">12" / 3200</div>
              <div className="text-sm text-ctc-blue">A4190</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">Jumbo M</div>
              <div className="text-sm text-ctc-blue">B5032</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="font-semibold text-gray-900 mb-1">Jumbo L</div>
              <div className="text-sm text-ctc-blue">B5025</div>
            </div>
          </div>
        </div>
      </section>

      {/* BIG Replacement Parts */}
      <section id="big-replacement-parts" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Box className="text-gray-700 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Filter Replacement Parts (BIG Series)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete replacement components for BIG Series filters. Includes bodies, inlets, heads, O-rings, and wedges.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Body Only</th>
                  <th className="px-4 py-3 text-left font-semibold">Inlet Only</th>
                  <th className="px-4 py-3 text-left font-semibold">Head Only</th>
                  <th className="px-4 py-3 text-left font-semibold">O-Ring Gasket</th>
                  <th className="px-4 py-3 text-left font-semibold">Blue Wedge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">3 NPT</td>
                  <td className="px-4 py-3 text-sm">B4475<br/><span className="text-gray-500">30 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4472<br/><span className="text-gray-500">5 lb</span></td>
                  <td className="px-4 py-3 text-sm">B578<br/><span className="text-gray-500">12 lb</span></td>
                  <td className="px-4 py-3 text-sm">A575</td>
                  <td className="px-4 py-3 text-sm">A174</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">3 FLG</td>
                  <td className="px-4 py-3 text-sm">B4473<br/><span className="text-gray-500">35 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4474<br/><span className="text-gray-500">11 lb</span></td>
                  <td className="px-4 py-3 text-sm">B578<br/><span className="text-gray-500">12 lb</span></td>
                  <td className="px-4 py-3 text-sm">A575</td>
                  <td className="px-4 py-3 text-sm">A174</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">4 NPT</td>
                  <td className="px-4 py-3 text-sm">B4527<br/><span className="text-gray-500">45 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4526<br/><span className="text-gray-500">9 lb</span></td>
                  <td className="px-4 py-3 text-sm">B612<br/><span className="text-gray-500">20 lb</span></td>
                  <td className="px-4 py-3 text-sm">A618</td>
                  <td className="px-4 py-3 text-sm">A619</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">4 FLG</td>
                  <td className="px-4 py-3 text-sm">B4477<br/><span className="text-gray-500">55 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4476<br/><span className="text-gray-500">13 lb</span></td>
                  <td className="px-4 py-3 text-sm">B612<br/><span className="text-gray-500">20 lb</span></td>
                  <td className="px-4 py-3 text-sm">A618</td>
                  <td className="px-4 py-3 text-sm">A619</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">6</td>
                  <td className="px-4 py-3 text-sm">B4529<br/><span className="text-gray-500">70 lb</span></td>
                  <td className="px-4 py-3 text-sm">B4528<br/><span className="text-gray-500">17 lb</span></td>
                  <td className="px-4 py-3 text-sm">B623<br/><span className="text-gray-500">22 lb</span></td>
                  <td className="px-4 py-3 text-sm">A620</td>
                  <td className="px-4 py-3 text-sm">A622</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">8</td>
                  <td className="px-4 py-3 text-sm">B4572<br/><span className="text-gray-500">122 lb</span></td>
                  <td className="px-4 py-3 text-sm">B4573<br/><span className="text-gray-500">22 lb</span></td>
                  <td className="px-4 py-3 text-sm">B1060<br/><span className="text-gray-500">30 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3779</td>
                  <td className="px-4 py-3 text-sm">A1073</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">10 S</td>
                  <td className="px-4 py-3 text-sm">B4586<br/><span className="text-gray-500">153 lb</span></td>
                  <td className="px-4 py-3 text-sm">B4587<br/><span className="text-gray-500">31 lb</span></td>
                  <td className="px-4 py-3 text-sm">B1079<br/><span className="text-gray-500">38 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3780</td>
                  <td className="px-4 py-3 text-sm">A763</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">10 M</td>
                  <td className="px-4 py-3 text-sm">B4588<br/><span className="text-gray-500">192 lb</span></td>
                  <td className="px-4 py-3 text-sm">B4587<br/><span className="text-gray-500">31 lb</span></td>
                  <td className="px-4 py-3 text-sm">B1079<br/><span className="text-gray-500">38 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3780</td>
                  <td className="px-4 py-3 text-sm">A763</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">10 L</td>
                  <td className="px-4 py-3 text-sm">B4589<br/><span className="text-gray-500">210 lb</span></td>
                  <td className="px-4 py-3 text-sm">B4587<br/><span className="text-gray-500">31 lb</span></td>
                  <td className="px-4 py-3 text-sm">B1079<br/><span className="text-gray-500">38 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3780</td>
                  <td className="px-4 py-3 text-sm">A763</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">12 / 3200</td>
                  <td className="px-4 py-3 text-sm">B4556<br/><span className="text-gray-500">278 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4557<br/><span className="text-gray-500">42 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4555<br/><span className="text-gray-500">42 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4678</td>
                  <td className="px-4 py-3 text-sm">A4874</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Approx. WT is approximate weight of the component. Bodies and heads include mounting hardware. Contact dealer for sizing guidance.
          </div>
        </div>
      </section>

      {/* BIG-S Parts Note */}
      <section id="big-s-parts" className="py-12 bg-blue-50 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-gray-300">
            <div className="flex items-start">
              <Box className="text-ctc-blue mr-4 flex-shrink-0 mt-1" size={32} />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">BIG-S (Stainless) Parts & Accessories</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <span className="font-semibold">Screen Element Compatibility:</span> BIG-S uses the same screen element SKUs and gasket standards as BIG for the corresponding body sizes.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  All standard BIG screens, gaskets, and cleaning system components (BIF, MVC, RAB, AVC) are compatible with BIG-S filter bodies. Refer to BIG Series parts sections above for screen and cleaning system part numbers.
                </p>
                <button
                  onClick={() => window.location.href = '/contact?topic=filtration&series=BIG-S'}
                  className="bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-ctc-blue-dark transition-colors inline-flex items-center"
                >
                  <Mail className="mr-2" size={18} />
                  Request BIG-S Part Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GNG Replacement Parts - Bodies / Heads / Screens */}
      <section id="gng-parts" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Box className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">GNG Replacement Parts — Bodies / Heads / Screens</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete replacement components for GNG Series filters. All parts listed omit pricing — contact your dealer or CTC for current availability and quotes.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Max Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">Body/Inlet (Item #)</th>
                  <th className="px-4 py-3 text-left font-semibold">Head Only (Item #)</th>
                  <th className="px-4 py-3 text-left font-semibold">Approx. WT (lb)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Only (Item #)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Dimensions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">500</td>
                  <td className="px-4 py-3 font-semibold">B3931</td>
                  <td className="px-4 py-3 font-semibold">A3927</td>
                  <td className="px-4 py-3">14</td>
                  <td className="px-4 py-3 font-semibold">A3929</td>
                  <td className="px-4 py-3">4″ × 24″</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">800</td>
                  <td className="px-4 py-3 font-semibold">B3196</td>
                  <td className="px-4 py-3 font-semibold">A3195</td>
                  <td className="px-4 py-3">20</td>
                  <td className="px-4 py-3 font-semibold">A3220</td>
                  <td className="px-4 py-3">6″ × 36″</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1000</td>
                  <td className="px-4 py-3 font-semibold">B3210</td>
                  <td className="px-4 py-3 font-semibold">B3209</td>
                  <td className="px-4 py-3">29</td>
                  <td className="px-4 py-3 font-semibold">A3221</td>
                  <td className="px-4 py-3">8″ × 48″</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1200</td>
                  <td className="px-4 py-3 font-semibold">B3185</td>
                  <td className="px-4 py-3 font-semibold">B3173</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3 font-semibold">B3222</td>
                  <td className="px-4 py-3">10″ × 60″</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">1500</td>
                  <td className="px-4 py-3 font-semibold">B3746</td>
                  <td className="px-4 py-3 font-semibold">B3173</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3 font-semibold">B3223</td>
                  <td className="px-4 py-3">10″ × 90″</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">2000</td>
                  <td className="px-4 py-3 font-semibold">B3969</td>
                  <td className="px-4 py-3 font-semibold">B3173</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3 font-semibold">B3982</td>
                  <td className="px-4 py-3">10″ × 120″</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Body Seals & Ring Locks</h3>
            <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Max Flow (GPM)</th>
                    <th className="px-4 py-3 text-left font-semibold">Body Seal (Item #)</th>
                    <th className="px-4 py-3 text-left font-semibold">Ring Lock (Item #)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">500</td>
                    <td className="px-4 py-3 font-semibold">A4263</td>
                    <td className="px-4 py-3 font-semibold">A3929</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">800</td>
                    <td className="px-4 py-3 font-semibold">A4264</td>
                    <td className="px-4 py-3 font-semibold">A3200</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">1000</td>
                    <td className="px-4 py-3 font-semibold">A4265</td>
                    <td className="px-4 py-3 font-semibold">A3199</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">1200–2000</td>
                    <td className="px-4 py-3 font-semibold">A4266</td>
                    <td className="px-4 py-3 font-semibold">A3198</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
            <p className="text-gray-800 leading-relaxed">
              <span className="font-semibold text-gray-900">GNG Parts Note:</span> Ring-lock head components and fasteners are model-specific. When ordering replacement parts, always specify your GNG model number and flow rate to ensure correct fitment.
            </p>
          </div>
        </div>
      </section>

      {/* IMB Brush Assemblies - BIG Series */}
      <section id="imb-brush-assemblies" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">IMB Brush Assemblies — BIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete brush assemblies and brush-only replacements for BIG Series cleaning systems.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">AA172</div>
                <div className="text-sm text-gray-600">Brush Only: A1115</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA173</div>
                <div className="text-sm text-gray-600">Brush Only: A1116</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA131</div>
                <div className="text-sm text-gray-600">Brush Only: A900</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA138</div>
                <div className="text-sm text-gray-600">Brush Only: A931</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA586</div>
                <div className="text-sm text-gray-600">Brush Only: A3988</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA492</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA493</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA494</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA656</div>
                <div className="text-sm text-gray-600">Brush Only: A4560</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMB Brush Assemblies - TIG Series */}
      <section id="tig-imb-brush-assemblies" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">IMB Brush Assemblies — TIG Series</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete brush assemblies and brush-only replacements for TIG Series cleaning systems.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA173</div>
                <div className="text-sm text-gray-600">Brush Only: A1116</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA131</div>
                <div className="text-sm text-gray-600">Brush Only: A900</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA138</div>
                <div className="text-sm text-gray-600">Brush Only: A931</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA586</div>
                <div className="text-sm text-gray-600">Brush Only: A3988</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BIF Kits - GNG Series */}
      <section id="gng-bif-kits" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Brush-in-Filter (BIF) Kits — GNG</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Manual brush cleaning kits for GNG Series inline filters. Hand-crank operated cleaning mechanism.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">500 GPM</div>
                <div className="font-semibold text-gray-900">BA752</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">800 GPM</div>
                <div className="font-semibold text-gray-900">BA592</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1000 GPM</div>
                <div className="font-semibold text-gray-900">BA591</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1200 GPM</div>
                <div className="font-semibold text-gray-900">BA590</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1500 GPM</div>
                <div className="font-semibold text-gray-900">BA589</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">2000 GPM</div>
                <div className="font-semibold text-gray-900">BA629</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RAB Kits - GNG Series */}
      <section id="gng-rab-kits" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Settings className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Retractable Auto Brush (RAB) — GNG</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Automatic brush cleaning systems for GNG Series. Sensor or timer-triggered automated cleaning.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1000 GPM</div>
                <div className="font-semibold text-gray-900">BA517</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1200 GPM</div>
                <div className="font-semibold text-gray-900">BA590</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">1500 GPM</div>
                <div className="font-semibold text-gray-900">BA629</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMB Brush Assemblies - GNG Series */}
      <section id="gng-imb-brush-assemblies" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">IMB Brush Assemblies — GNG</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete brush assemblies and brush-only replacements for GNG Series cleaning systems.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA613</div>
                <div className="text-sm text-gray-600">Brush Only: A900</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA411</div>
                <div className="text-sm text-gray-600">Brush Only: A931</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA587</div>
                <div className="text-sm text-gray-600">Brush Only: A3988</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA496</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA495</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-gray-900">BA612</div>
                <div className="text-sm text-gray-600">Brush Only: A3607</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Replacement Screens BIG Series Table */}
      <section id="replacement-screens-table" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Filter className="text-ctc-blue mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Replacement Screens (BIG Series)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Detailed replacement screen specifications for BIG Series filters. Available in perforated plate or mesh configurations.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                  <th className="px-4 py-3 text-left font-semibold">Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">2"</td>
                  <td className="px-4 py-3 text-sm">B589</td>
                  <td className="px-4 py-3 text-sm">12</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">3"</td>
                  <td className="px-4 py-3 text-sm">B148</td>
                  <td className="px-4 py-3 text-sm">18</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">4"</td>
                  <td className="px-4 py-3 text-sm">B617</td>
                  <td className="px-4 py-3 text-sm">24</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">6"</td>
                  <td className="px-4 py-3 text-sm">C630</td>
                  <td className="px-4 py-3 text-sm">36</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">8"</td>
                  <td className="px-4 py-3 text-sm">C1075</td>
                  <td className="px-4 py-3 text-sm">48</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">10" S</td>
                  <td className="px-4 py-3 text-sm">D1082S</td>
                  <td className="px-4 py-3 text-sm">60</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">10" M</td>
                  <td className="px-4 py-3 text-sm">D1082M</td>
                  <td className="px-4 py-3 text-sm">90</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">10" L</td>
                  <td className="px-4 py-3 text-sm">D1082L</td>
                  <td className="px-4 py-3 text-sm">120</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 font-semibold">12" / 3200</td>
                  <td className="px-4 py-3 text-sm">A4190</td>
                  <td className="px-4 py-3 text-sm">168</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                  <td className="px-4 py-3 text-sm text-gray-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Perf and Mesh columns show available perforation patterns and mesh sizes. Contact dealer for specific configurations and custom options.
          </div>
        </div>
      </section>

      {/* Original Replacement Screens Section (keep for TIG and other compatibility) */}
      <section id="inline-screens" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Filter className="text-ctc-blue mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Replacement Screens (Inline Filters)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Compatible with BIG Series and TIG Series inline filters. Screens available in 20-120 mesh with 304 stainless steel construction.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Screen Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits Models/Sizes</th>
                  <th className="px-4 py-3 text-left font-semibold">Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Mesh/Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Material</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-2-20</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">18-36</td>
                  <td className="px-4 py-3 text-sm">20 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Coarse filtration</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-2-50</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">18-36</td>
                  <td className="px-4 py-3 text-sm">50 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Medium filtration</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-2-80</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">18-36</td>
                  <td className="px-4 py-3 text-sm">80 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Fine filtration</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-2-120</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">18-36</td>
                  <td className="px-4 py-3 text-sm">120 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Very fine, emitter protection</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-3-50</td>
                  <td className="px-4 py-3 text-sm">BIG 3"</td>
                  <td className="px-4 py-3 text-sm">24-48</td>
                  <td className="px-4 py-3 text-sm">50 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Standard agricultural</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-BIG-4-50</td>
                  <td className="px-4 py-3 text-sm">BIG 4"</td>
                  <td className="px-4 py-3 text-sm">30-60</td>
                  <td className="px-4 py-3 text-sm">50 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Medium flow systems</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-TIG-2-80</td>
                  <td className="px-4 py-3 text-sm">TIG 2"</td>
                  <td className="px-4 py-3 text-sm">12-24</td>
                  <td className="px-4 py-3 text-sm">80 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">Compact inline, gasketed</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">SCR-TIG-3-80</td>
                  <td className="px-4 py-3 text-sm">TIG 3"</td>
                  <td className="px-4 py-3 text-sm">18-36</td>
                  <td className="px-4 py-3 text-sm">80 mesh</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">True inline design</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Length is customizable to fit your specific filter body. Contact dealer with existing screen length or filter model for exact replacement.
          </div>
        </div>
      </section>

      {/* Brush Assemblies */}
      <section id="brush-assemblies" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Brush Assemblies (BIF/RAB)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement brush assemblies for BIG Series with brush cleaning. Complete assembly includes motor mount, brush head, and bristles.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Brush Assembly #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits Models/Sizes</th>
                  <th className="px-4 py-3 text-left font-semibold">Bristle Type</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">BRU-BIG-2-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 2" (BIF/RAB)</td>
                  <td className="px-4 py-3 text-sm">Nylon standard</td>
                  <td className="px-4 py-3 text-sm">General purpose, most applications</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">BRU-BIG-2-HD</td>
                  <td className="px-4 py-3 text-sm">BIG 2" (BIF/RAB)</td>
                  <td className="px-4 py-3 text-sm">Nylon heavy-duty</td>
                  <td className="px-4 py-3 text-sm">High debris loading, aggressive cleaning</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">BRU-BIG-3-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 3" (BIF/RAB)</td>
                  <td className="px-4 py-3 text-sm">Nylon standard</td>
                  <td className="px-4 py-3 text-sm">Agricultural irrigation systems</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">BRU-BIG-4-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 4" (BIF/RAB)</td>
                  <td className="px-4 py-3 text-sm">Nylon standard</td>
                  <td className="px-4 py-3 text-sm">Large flow applications</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">BRU-BIG-4-HD</td>
                  <td className="px-4 py-3 text-sm">BIG 4" (BIF/RAB)</td>
                  <td className="px-4 py-3 text-sm">Nylon heavy-duty</td>
                  <td className="px-4 py-3 text-sm">Industrial applications, heavy solids</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Brush life: 1-3 years depending on usage. Replace when bristles show wear or cleaning efficiency decreases. Complete assembly includes mounting hardware.
          </div>
        </div>
      </section>

      {/* Vacuum Nozzles/Arms */}
      <section id="vacuum-nozzles" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Wrench className="text-orange-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Vacuum Nozzles/Arms (MVC/AVC)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement vacuum nozzles and suction arms for BIG Series with automatic vacuum cleaning systems.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Nozzle/Arm #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits Models/Sizes</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-NOZ-2-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 2" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Standard vacuum nozzle, 1/2" port</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-ARM-2-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 2" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Complete suction arm assembly</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-NOZ-3-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 3" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Standard vacuum nozzle, 3/4" port</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-ARM-3-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 3" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Complete suction arm assembly</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-NOZ-4-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 4" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Standard vacuum nozzle, 1" port</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">VAC-ARM-4-STD</td>
                  <td className="px-4 py-3 text-sm">BIG 4" (MVC/AVC)</td>
                  <td className="px-4 py-3 text-sm">Complete suction arm assembly, heavy-duty</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Vacuum nozzles should be inspected annually for wear. Replace if clogged or damaged. Suction arms include pivot bearings and mounting hardware.
          </div>
        </div>
      </section>

      {/* Gaskets & O-Rings */}
      <section id="gaskets-orings" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Shield className="text-blue-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Gaskets & O-Rings</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement gaskets and O-rings for all filter models. EPDM rubber standard, Viton available for high-temperature applications.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits Models/Sizes</th>
                  <th className="px-4 py-3 text-left font-semibold">Material</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-BIG-2-EPDM</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Head/body seal, standard temp</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-BIG-2-VIT</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">Viton</td>
                  <td className="px-4 py-3 text-sm">High-temperature applications</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-BIG-3-EPDM</td>
                  <td className="px-4 py-3 text-sm">BIG 3"</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Head/body seal, standard temp</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-BIG-4-EPDM</td>
                  <td className="px-4 py-3 text-sm">BIG 4"</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Head/body seal, standard temp</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-TIG-2-EPDM</td>
                  <td className="px-4 py-3 text-sm">TIG 2"</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Gasketed union seal</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">GSK-TIG-3-EPDM</td>
                  <td className="px-4 py-3 text-sm">TIG 3"</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Gasketed union seal</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">ORING-KIT-STD</td>
                  <td className="px-4 py-3 text-sm">All models</td>
                  <td className="px-4 py-3 text-sm">EPDM</td>
                  <td className="px-4 py-3 text-sm">Assorted O-rings for service</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Gaskets should be replaced annually or when leaks occur. Always lubricate with food-grade silicone before installation. Keep spare gaskets on hand for emergency service.
          </div>
        </div>
      </section>

      {/* Blue Wedges */}
      <section id="blue-wedges" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Package className="text-ctc-blue mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Blue Wedges</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Screen retention wedges for BIG and TIG series. High-density polyethylene (HDPE) construction, blue color for easy identification.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits</th>
                  <th className="px-4 py-3 text-left font-semibold">Qty per Assembly</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-BIG-2</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">4</td>
                  <td className="px-4 py-3 text-sm">Standard 2" screen retention</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-BIG-3</td>
                  <td className="px-4 py-3 text-sm">BIG 3"</td>
                  <td className="px-4 py-3 text-sm">4</td>
                  <td className="px-4 py-3 text-sm">Standard 3" screen retention</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-BIG-4</td>
                  <td className="px-4 py-3 text-sm">BIG 4"</td>
                  <td className="px-4 py-3 text-sm">6</td>
                  <td className="px-4 py-3 text-sm">Heavy-duty 4" screen retention</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-TIG-2</td>
                  <td className="px-4 py-3 text-sm">TIG 2"</td>
                  <td className="px-4 py-3 text-sm">4</td>
                  <td className="px-4 py-3 text-sm">Gasketed design retention</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-TIG-3</td>
                  <td className="px-4 py-3 text-sm">TIG 3"</td>
                  <td className="px-4 py-3 text-sm">4</td>
                  <td className="px-4 py-3 text-sm">Gasketed design retention</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">WEDGE-KIT-ASSORTED</td>
                  <td className="px-4 py-3 text-sm">All sizes</td>
                  <td className="px-4 py-3 text-sm">24 (assorted)</td>
                  <td className="px-4 py-3 text-sm">Service kit, 2-4" sizes mixed</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Wedges should be replaced if cracked, worn, or deformed. Always use correct quantity per assembly for proper screen retention. Keep spares on hand for field service.
          </div>
        </div>
      </section>

      {/* Heads & Bodies */}
      <section id="heads-bodies" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Box className="text-gray-700 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Heads & Bodies</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement filter heads and bodies. 304 stainless steel or 316 SS for corrosive environments. Custom fabrication available.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Model/Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Material</th>
                  <th className="px-4 py-3 text-left font-semibold">Approx. Weight</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">HEAD-BIG-2-304</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">15 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard head assembly</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">BODY-BIG-2-304</td>
                  <td className="px-4 py-3 text-sm">BIG 2"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">25 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard body, 24" length</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">HEAD-BIG-3-304</td>
                  <td className="px-4 py-3 text-sm">BIG 3"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">25 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard head assembly</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">BODY-BIG-3-304</td>
                  <td className="px-4 py-3 text-sm">BIG 3"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">45 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard body, 36" length</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">HEAD-BIG-4-304</td>
                  <td className="px-4 py-3 text-sm">BIG 4"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">40 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard head assembly</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">BODY-BIG-4-304</td>
                  <td className="px-4 py-3 text-sm">BIG 4"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">75 lbs</td>
                  <td className="px-4 py-3 text-sm">Standard body, 48" length</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">HEAD-TIG-2-304</td>
                  <td className="px-4 py-3 text-sm">TIG 2"</td>
                  <td className="px-4 py-3 text-sm">304 SS</td>
                  <td className="px-4 py-3 text-sm">8 lbs</td>
                  <td className="px-4 py-3 text-sm">Compact gasketed design</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> 316 SS available for corrosive environments (add -316 suffix). Custom lengths available—specify required body length. Heads include mounting hardware.
          </div>
        </div>
      </section>

      {/* TIG Replacement Parts */}
      <section id="tig-replacement-parts" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Box className="text-gray-700 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Filter Replacement Parts (TIG Series)</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Complete replacement components for TIG Series filters. Includes bodies, heads, O-rings, and wedges.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Size</th>
                  <th className="px-4 py-3 text-left font-semibold">Body Only</th>
                  <th className="px-4 py-3 text-left font-semibold">Head Only</th>
                  <th className="px-4 py-3 text-left font-semibold">O-Ring Gasket</th>
                  <th className="px-4 py-3 text-left font-semibold">Blue Wedge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">3 THRD</td>
                  <td className="px-4 py-3 text-sm">B3963<br/><span className="text-gray-500">20 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3964<br/><span className="text-gray-500">10 lb</span></td>
                  <td className="px-4 py-3 text-sm">A575</td>
                  <td className="px-4 py-3 text-sm">A174</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">3 FLG</td>
                  <td className="px-4 py-3 text-sm">B3986<br/><span className="text-gray-500">20 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3964<br/><span className="text-gray-500">10 lb</span></td>
                  <td className="px-4 py-3 text-sm">A575</td>
                  <td className="px-4 py-3 text-sm">A174</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">4 THRD</td>
                  <td className="px-4 py-3 text-sm">B3987<br/><span className="text-gray-500">29 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3971<br/><span className="text-gray-500">12 lb</span></td>
                  <td className="px-4 py-3 text-sm">A618</td>
                  <td className="px-4 py-3 text-sm">A619</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">4 FLG</td>
                  <td className="px-4 py-3 text-sm">B3970<br/><span className="text-gray-500">29 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3971<br/><span className="text-gray-500">12 lb</span></td>
                  <td className="px-4 py-3 text-sm">A618</td>
                  <td className="px-4 py-3 text-sm">A619</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">6</td>
                  <td className="px-4 py-3 text-sm">B3983<br/><span className="text-gray-500">75 lb</span></td>
                  <td className="px-4 py-3 text-sm">B3985<br/><span className="text-gray-500">18 lb</span></td>
                  <td className="px-4 py-3 text-sm">A620</td>
                  <td className="px-4 py-3 text-sm">A622</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">8</td>
                  <td className="px-4 py-3 text-sm">B4032<br/><span className="text-gray-500">100 lb</span></td>
                  <td className="px-4 py-3 text-sm">A4031<br/><span className="text-gray-500">28 lb</span></td>
                  <td className="px-4 py-3 text-sm">A3779</td>
                  <td className="px-4 py-3 text-sm">A1073</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Approx. WT is approximate weight of the component. Bodies and heads include mounting hardware. Contact dealer for sizing guidance.
          </div>
        </div>
      </section>

      {/* SCS2 Intake Parts */}
      <section id="scs2-parts" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Filter className="text-blue-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">SCS2 Self-Cleaning Intake Parts</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement parts for SCS2 self-cleaning suction screens with internal rotating spray bar. All parts size-specific to model.
          </p>

          {/* Replacement Screens */}
          <h3 className="text-xl font-bold text-gray-900 mb-4">Replacement Screens</h3>
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg mb-8">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">GPM</th>
                  <th className="px-4 py-3 text-left font-semibold">Item #</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">100</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3879</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">200</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3880</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">300</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3882</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">400</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3888</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">600</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3889</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">800</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3531</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">1000</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3457</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">1200</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3458</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">1400</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3462</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">1600</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3463</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">2000</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3466</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">2400</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3470</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">3000</td>
                  <td className="px-4 py-3 text-sm font-semibold">B3476</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3 text-sm">3600</td>
                  <td className="px-4 py-3 text-sm font-semibold">C3630</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Other SCS Parts */}
          <h3 className="text-xl font-bold text-gray-900 mb-4">Other SCS Parts & Accessories</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Spray & Drive Nozzles</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Spray Nozzles (ea):</span>
                  <span className="font-semibold text-ctc-blue">A5151</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Drive Nozzles (ea):</span>
                  <span className="font-semibold text-ctc-blue">A5150</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Center Tube Components</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Center Tube Plug:</span>
                  <span className="font-semibold text-ctc-blue">A3796</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Center Tube Adaptor ¾":</span>
                  <span className="font-semibold text-ctc-blue">A3883</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Center Tube Adaptor 1":</span>
                  <span className="font-semibold text-ctc-blue">A3231</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Seals & Bearings</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Standard Seals (ea):</span>
                  <span className="font-semibold text-ctc-blue">A4128</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Standard Bearings (ea):</span>
                  <span className="font-semibold text-ctc-blue">A4119</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Harsh Enviro Seals (ea):</span>
                  <span className="font-semibold text-ctc-blue">A4489</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Harsh Enviro Bearings (ea):</span>
                  <span className="font-semibold text-ctc-blue">A4515</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Deflection Cones</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">16":</span>
                  <span className="font-semibold text-ctc-blue">B4919</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">24":</span>
                  <span className="font-semibold text-ctc-blue">B4882</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">30":</span>
                  <span className="text-gray-500 italic">Contact for part #</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">36":</span>
                  <span className="text-gray-500 italic">Contact for part #</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Spray Bars & Center Tubes</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">All Sizes:</span>
                  <span className="text-gray-500 italic">Please call</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Screen life: 5-10 years. Bearing life: 3-5 years standard, longer with sealed option. Spray bar nozzles should be cleaned quarterly. Contact dealer for complete parts list by model.
          </div>
        </div>
      </section>

      {/* PSF Accessories */}
      <section id="psf-accessories" className="py-16 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Shield className="text-green-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">PSF Passive Screen Accessories</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Accessories for PSF passive fish-friendly screens. Protective hoops, stands, and mounting hardware.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-green-600 to-green-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">HOOP-PSF-3</td>
                  <td className="px-4 py-3 text-sm">PSF-3</td>
                  <td className="px-4 py-3 text-sm">Protective hoops, 304 SS, set of 3</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">HOOP-PSF-4</td>
                  <td className="px-4 py-3 text-sm">PSF-4</td>
                  <td className="px-4 py-3 text-sm">Protective hoops, 304 SS, set of 4</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">HOOP-PSF-8-REQ</td>
                  <td className="px-4 py-3 text-sm">PSF-8</td>
                  <td className="px-4 py-3 text-sm">Protective hoops, required for 8", set of 5</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">HOOP-PSF-10-REQ</td>
                  <td className="px-4 py-3 text-sm">PSF-10</td>
                  <td className="px-4 py-3 text-sm">Protective hoops, required for 10", set of 6</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">STAND-PSF-FLOAT</td>
                  <td className="px-4 py-3 text-sm">All PSF</td>
                  <td className="px-4 py-3 text-sm">Floating stand assembly with collar</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">STAND-PSF-FIXED</td>
                  <td className="px-4 py-3 text-sm">All PSF</td>
                  <td className="px-4 py-3 text-sm">Fixed mounting stand, bottom-mounted</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">MOUNT-KIT-FLANGE</td>
                  <td className="px-4 py-3 text-sm">All PSF</td>
                  <td className="px-4 py-3 text-sm">Flange mounting kit, specify size</td>
                </tr>
                <tr className="hover:bg-green-50">
                  <td className="px-4 py-3 text-sm">MOUNT-KIT-COLLAR</td>
                  <td className="px-4 py-3 text-sm">All PSF</td>
                  <td className="px-4 py-3 text-sm">Collar mounting kit, bolt-on, specify pipe OD</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Hoops recommended for 8" and required for 10" models. Mounting kits are custom-fabricated—provide pipe OD or flange specs. Anti-vortex plates available on request.
          </div>
        </div>
      </section>

      {/* BWB Water Box Parts */}
      <section id="bwb-parts" className="py-16 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center mb-8">
            <Box className="text-orange-600 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">BWB Water Box Parts</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Replacement parts for BWB Water Box filters. Screen baskets, gaskets, and access door components.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part #</th>
                  <th className="px-4 py-3 text-left font-semibold">Fits</th>
                  <th className="px-4 py-3 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">BASK-BWB-200-50</td>
                  <td className="px-4 py-3 text-sm">BWB-200</td>
                  <td className="px-4 py-3 text-sm">Screen basket, 50 mesh, 304 SS</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">BASK-BWB-400-50</td>
                  <td className="px-4 py-3 text-sm">BWB-400</td>
                  <td className="px-4 py-3 text-sm">Screen basket, 50 mesh, 304 SS</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">BASK-BWB-700-50</td>
                  <td className="px-4 py-3 text-sm">BWB-700</td>
                  <td className="px-4 py-3 text-sm">Screen basket, 50 mesh, 304 SS</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">BASK-BWB-1000-50</td>
                  <td className="px-4 py-3 text-sm">BWB-1000</td>
                  <td className="px-4 py-3 text-sm">Screen basket, 50 mesh, 304 SS</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">GSK-BWB-DOOR</td>
                  <td className="px-4 py-3 text-sm">All BWB</td>
                  <td className="px-4 py-3 text-sm">Access door gasket, EPDM</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">GSK-BWB-INLET</td>
                  <td className="px-4 py-3 text-sm">All BWB</td>
                  <td className="px-4 py-3 text-sm">Inlet flange gasket, size-specific</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 text-sm">HDWR-BWB-DOOR</td>
                  <td className="px-4 py-3 text-sm">All BWB</td>
                  <td className="px-4 py-3 text-sm">Door hardware kit, bolts/latches</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <span className="font-semibold">Note:</span> Screen baskets available in 20-120 mesh. Specify mesh size when ordering. Baskets typically have 5-10 year service life. Door gaskets should be replaced every 2-3 years or if leaking.
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help Finding the Right Parts?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our parts specialists can help you identify the correct replacement parts for your filtration system. Contact us with your model number, serial number, or photos for fast identification and quotes.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=parts"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Request Parts Quote
            </Link>

            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Download className="mr-2" size={20} />
              Download Full Catalog
            </a>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FiltrationPartsPage;
